/**
 * A readable family tree. On a wide screen (or three generations) it's a bracket, sires above dams;
 * on a phone with more generations it becomes an outline you read top to bottom. Missing ancestors
 * say "Unknown": a gap in the pedigree is never shown as "no relation".
 */
import { Link } from "react-router";
import { formatDate } from "../../../web/lib/format";
import { t } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { Chip, ErrorNote, Icon, Loading, Segmented, cx, useIsWide, useLocalState } from "../../../web/ui/kit";
import { associationShort } from "../shared/epd";
import { percent } from "../shared/pedigree";
import type { CorePedigree, PedigreeExtras } from "../shared/types";

type Slot = { id: string | null; role: "root" | "sire" | "dam"; childId: string | null };

/** Slots by generation: gen g has 2^g slots; slot 2k is the sire of slot k in the generation before, 2k+1 the dam. */
export function slots(core: CorePedigree, rootId: string, depth: number): Slot[][] {
  const gens: Slot[][] = [[{ id: rootId, role: "root", childId: null }]];
  for (let g = 1; g <= depth; g++) {
    const prev = gens[g - 1];
    const next: Slot[] = [];
    for (const s of prev) {
      const n = s.id ? core.nodes[s.id] : undefined;
      next.push({ id: n?.sire_id ?? null, role: "sire", childId: s.id }, { id: n?.dam_id ?? null, role: "dam", childId: s.id });
    }
    gens.push(next);
  }
  return gens;
}

function Flag({ status }: { status: string | undefined }) {
  if (!status) return null;
  if (status === "confirmed" || status === "verified") return <span className="genetics-node-flag tone-good" title={t("Parentage verified")}><Icon name="check" size={13} />{t("Verified")}</span>;
  if (status === "excluded") return <span className="genetics-node-flag tone-bad" title={t("Parentage excluded")}><Icon name="warn" size={13} />{t("Excluded")}</span>;
  if (status === "suspected") return <span className="genetics-node-flag tone-warn"><Icon name="help" size={13} />{t("Suspected")}</span>;
  return null;
}

export function PedNode({ slot, core, extras, compact }: { slot: Slot; core: CorePedigree; extras?: PedigreeExtras; compact?: boolean }) {
  const role = slot.role === "sire" ? t("Sire") : slot.role === "dam" ? t("Dam") : "";
  const cls = cx("genetics-node", slot.role === "root" && "genetics-node-root", slot.role === "sire" && "genetics-node-sire", slot.role === "dam" && "genetics-node-dam");
  if (!slot.id) return <div className={cx(cls, "genetics-node-unknown")} aria-label={`${role}: ${t("Unknown")}`}><span>{t("Unknown")}</span>{!compact && role && <span className="genetics-node-meta">{role}</span>}</div>;
  const n = core.nodes[slot.id];
  const x = extras?.nodes[slot.id];
  const flag = slot.childId && slot.role !== "root" ? extras?.nodes[slot.childId]?.parentage[slot.role as "sire" | "dam"] : undefined;
  if (!n) return <div className={cls}><span className="genetics-node-name">…</span></div>;
  return (
    <Link to={`/cattle/${n.id}`} className={cls} aria-label={role ? `${role}: ${n.label}` : n.label}>
      <span className="genetics-node-name">{n.label}</span>
      <span className="genetics-node-meta">
        {x?.registration && <span className="mono">{x.association ? `${associationShort(x.association)} ` : ""}{x.registration}</span>}
        {!compact && n.birth_date && <span>{formatDate(n.birth_date.slice(0, 4))}</span>}
        {n.status === "reference" && <span>{t("Reference")}</span>}
        <Flag status={flag} />
      </span>
    </Link>
  );
}

export function Bracket({ core, extras, rootId, depth, showRoot = true }: { core: CorePedigree; extras?: PedigreeExtras; rootId: string; depth: number; showRoot?: boolean }) {
  const gens = slots(core, rootId, depth);
  const first = showRoot ? 0 : 1;
  const cols = depth + 1 - first;
  const rows = 2 ** depth;
  return (
    <div className="genetics-bracket-scroll">
      <div className="genetics-bracket" style={{ ["--gens" as string]: cols, gridTemplateRows: `repeat(${rows}, minmax(44px, auto))` }}>
        {gens.slice(first).map((gen, gi) => {
          const g = gi + first;
          const span = 2 ** (depth - g);
          return gen.map((slot, s) => (
            <div key={`${g}-${s}`} style={{ gridColumn: gi + 1, gridRow: `${s * span + 1} / span ${span}`, minWidth: 0 }}>
              <PedNode slot={slot} core={core} extras={extras} compact={g === depth && depth > 3} />
            </div>
          ));
        })}
      </div>
    </div>
  );
}

function OutlineBranch({ id, role, childId, core, extras, depth }: { id: string | null; role: "sire" | "dam"; childId: string | null; core: CorePedigree; extras?: PedigreeExtras; depth: number }) {
  const n = id ? core.nodes[id] : undefined;
  return (
    <li>
      <div className="genetics-outline-role">{role === "sire" ? t("Sire") : t("Dam")}</div>
      <PedNode slot={{ id, role, childId }} core={core} extras={extras} compact={!id} />
      {depth > 1 && n && (n.sire_id || n.dam_id) && (
        <ul className="genetics-outline">
          <OutlineBranch id={n.sire_id} role="sire" childId={n.id} core={core} extras={extras} depth={depth - 1} />
          <OutlineBranch id={n.dam_id} role="dam" childId={n.id} core={core} extras={extras} depth={depth - 1} />
        </ul>
      )}
    </li>
  );
}

export function Outline({ core, extras, rootId, depth }: { core: CorePedigree; extras?: PedigreeExtras; rootId: string; depth: number }) {
  const root = core.nodes[rootId];
  return (
    <ul className="genetics-outline">
      <OutlineBranch id={root?.sire_id ?? null} role="sire" childId={rootId} core={core} extras={extras} depth={depth} />
      <OutlineBranch id={root?.dam_id ?? null} role="dam" childId={rootId} core={core} extras={extras} depth={depth} />
    </ul>
  );
}

export function PedigreeSummary({ extras }: { extras: PedigreeExtras }) {
  return (
    <div className="genetics-ped-summary">
      <span>{t("{{known}} of {{possible}} ancestors known", { known: extras.completeness.known, possible: extras.completeness.possible })}</span>
      <span>{extras.inbreeding > 0 ? t("Inbreeding {{value}} in the known pedigree", { value: percent(extras.inbreeding) }) : t("No inbreeding found in the known pedigree")}</span>
      {extras.completeness.known < extras.completeness.possible && <Chip tone="neutral" icon="info">{t("Gaps are unknown, not unrelated")}</Chip>}
    </div>
  );
}

/** Fetches the canonical pedigree and the genetics details for it, and lets the reader pick 3 to 5 generations. */
export function PedigreeView({ animalId, defaultDepth = 3, showRoot = true, pickDepth = true }: { animalId: string; defaultDepth?: number; showRoot?: boolean; pickDepth?: boolean }) {
  const wide = useIsWide();
  const [saved, setSaved] = useLocalState<string>("genetics.pedigreeDepth", String(defaultDepth));
  const depth = pickDepth ? Number(saved) || defaultDepth : defaultDepth;
  const core = useApi<CorePedigree>(`/animals/${animalId}/pedigree?depth=${depth}`);
  const extras = useApi<PedigreeExtras>(`/m/genetics/pedigree/${animalId}/extras?depth=${depth}`);
  if (core.isLoading) return <Loading />;
  if (!core.data) return <ErrorNote error={core.error} retry={() => core.refetch()} />;
  // A phone fits about three columns; deeper trees read better as an outline.
  const outline = !wide && depth + (showRoot ? 1 : 0) > 3;
  return (
    <div className="stack">
      {pickDepth && (
        <Segmented label={t("Generations")} value={String(depth)} onChange={(v) => setSaved(v)}
          options={[3, 4, 5].map((n) => ({ value: String(n), label: t("{{count}} generations", { count: n }) }))} />
      )}
      {outline
        ? <Outline core={core.data} extras={extras.data} rootId={animalId} depth={depth} />
        : <Bracket core={core.data} extras={extras.data} rootId={animalId} depth={depth} showRoot={showRoot} />}
      {extras.data && <PedigreeSummary extras={extras.data} />}
    </div>
  );
}
